import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ExternalLink, Menu, PhoneCall, X } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  abroadNavigation,
  admissionNavigation,
  brand,
  contactInfo,
  primaryNavigation
} from "../../data/navigation";
import PrimaryButton from "../ui/PrimaryButton";

function Navbar() {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isAdmissionsOpen, setIsAdmissionsOpen] = useState(false);
  const [isAbroadOpen, setIsAbroadOpen] = useState(false);
  const currentYear = new Date().getFullYear();

  const isAdmissionsRoute = admissionNavigation.some((item) => item.to === location.pathname);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };

    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const desktopLinkClass = ({ isActive }) =>
    `relative text-sm font-semibold transition-all duration-300 ${
      isActive
        ? "text-brand-gold"
        : "text-brand-charcoal/75 hover:text-brand-navy"
    }`;

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      {/* Top bar */}
      <div className="bg-gradient-to-r from-brand-navy-deep to-brand-navy px-4 py-2 text-center text-[11px] font-semibold text-white/90 sm:text-xs">
        <span className="inline-flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald animate-pulse" />
          {`Admissions ${currentYear} Guidance Open - Students & Parents can book direct counselling`}
        </span>
      </div>

      <div className={`px-3 transition-all duration-500 ${isScrolled ? "pt-3" : "pt-4"}`}>
        <nav
          className={`mx-auto flex w-full max-w-7xl items-center justify-between rounded-2xl border px-4 py-3 transition-all duration-500 md:px-6 ${
            isScrolled
              ? "border-brand-line/60 bg-white shadow-premium"
              : "border-brand-line/60 bg-white shadow-card"
          }`}
        >
          {/* Logo */}
          <Link className="flex items-center gap-3" to="/">
            <img
              src="/TCS TCS.png"
              alt={`${brand.shortName} Logo`}
              className="h-28 w-auto object-contain transition-all duration-300"
              style={{
                filter: "brightness(0) saturate(100%) invert(10%) sepia(35%) saturate(3015%) hue-rotate(202deg) brightness(96%) contrast(100%)"
              }}
            />
            <div>
              <p className="text-xl font-extrabold leading-none text-brand-navy transition-colors duration-300 sm:text-2xl">
                {brand.shortName}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-charcoal/55 transition-colors duration-300">
                Education
              </p>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden items-center gap-7 lg:flex">
            {/* Primary nav links (Home, About, Colleges) */}
            {primaryNavigation.map((item) => (
              <NavLink key={item.to} className={desktopLinkClass} to={item.to}>
                {item.label}
              </NavLink>
            ))}

            {/* Abroad dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsAbroadOpen(true)}
              onMouseLeave={() => setIsAbroadOpen(false)}
            >
              <button
                type="button"
                className="inline-flex items-center gap-1 text-sm font-semibold transition-all duration-300 text-brand-charcoal/75 hover:text-brand-navy"
                onClick={() => setIsAbroadOpen((prev) => !prev)}
              >
                Abroad
                <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isAbroadOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {isAbroadOpen ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 top-full mt-4 w-64 rounded-2xl border border-brand-line/50 bg-white p-3 shadow-premium"
                  >
                    <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-brand-charcoal/45">
                      Study Destinations
                    </p>
                    {abroadNavigation.map((item) => (
                      <a
                        key={item.label}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-brand-charcoal transition-all duration-200 hover:bg-brand-gold-soft/30 hover:text-brand-navy"
                      >
                        <span className="text-base">{item.flag}</span>
                        {item.label}
                        <ExternalLink className="ml-auto h-3 w-3 text-brand-charcoal/40" />
                      </a>
                    ))}
                    <div className="mt-2 border-t border-brand-line/40 pt-2">
                      <a
                        href="https://nexteraabroad.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 rounded-xl px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-gold-strong transition-all duration-200 hover:bg-brand-gold-soft/20"
                      >
                        Visit NextEra Abroad
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>

            {/* Pathways dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsAdmissionsOpen(true)}
              onMouseLeave={() => setIsAdmissionsOpen(false)}
            >
              <button
                type="button"
                className={`inline-flex items-center gap-1 text-sm font-semibold transition-all duration-300 ${
                  isAdmissionsRoute
                    ? "text-brand-gold"
                    : "text-brand-charcoal/75 hover:text-brand-navy"
                }`}
                onClick={() => setIsAdmissionsOpen((prev) => !prev)}
              >
                Pathways
                <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isAdmissionsOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {isAdmissionsOpen ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 top-full mt-4 w-72 rounded-2xl border border-brand-line/50 bg-white p-3 shadow-premium"
                  >
                    <a
                      href="https://nexteraabroad.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between rounded-xl px-4 py-2.5 text-sm font-semibold text-brand-charcoal transition-all duration-200 hover:bg-brand-gold-soft/30 hover:text-brand-navy"
                    >
                      Undergraduate Abroad
                      <ExternalLink className="h-3.5 w-3.5 text-brand-gold-strong" />
                    </a>
                    <div className="my-1 border-t border-brand-line/40" />
                    {admissionNavigation.map((item) => (
                      <NavLink
                        key={item.to}
                        className={({ isActive }) =>
                          `block rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                            isActive
                              ? "text-brand-gold"
                              : "text-brand-charcoal hover:bg-brand-gold-soft/30 hover:text-brand-navy"
                          }`
                        }
                        to={item.to}
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>

            {/* Contact link */}
            <NavLink className={desktopLinkClass} to="/contact">
              Contact
            </NavLink>
          </div>

          {/* Desktop CTA */}
          <div className="hidden items-center gap-3 lg:flex">
            <a
              className="inline-flex items-center gap-2 rounded-xl border border-brand-line px-4 py-2.5 text-xs font-bold text-brand-navy transition-all duration-300 hover:border-brand-gold/30 hover:bg-brand-gold-soft/20"
              href={`tel:${contactInfo.phoneHref}`}
            >
              <PhoneCall className="h-3.5 w-3.5" />
              {contactInfo.phoneDisplay}
            </a>
            <PrimaryButton to="/contact">
              Enquiry Now
            </PrimaryButton>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-brand-line text-brand-navy transition-all duration-300 lg:hidden"
            onClick={() => setIsMobileOpen((prev) => !prev)}
          >
            {isMobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>

        {/* Mobile menu */}
        <AnimatePresence>
          {isMobileOpen ? (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.98 }}
              transition={{ duration: 0.25 }}
              className="mx-auto mt-3 w-full max-w-7xl rounded-2xl border border-brand-line/50 bg-white p-5 shadow-premium lg:hidden"
            >
              {/* Primary links */}
              <div className="space-y-1">
                {primaryNavigation.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setIsMobileOpen(false)}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                        isActive ? "text-brand-gold" : "text-brand-navy hover:bg-brand-sky/40"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <NavLink
                  to="/contact"
                  onClick={() => setIsMobileOpen(false)}
                  className={({ isActive }) =>
                    `block rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                      isActive ? "text-brand-gold" : "text-brand-navy hover:bg-brand-sky/40"
                    }`
                  }
                >
                  Contact
                </NavLink>
              </div>

              {/* Abroad destinations */}
              <div className="mt-5 rounded-xl border border-brand-line/40 bg-gradient-to-r from-brand-sky/30 to-transparent p-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/60">
                  Study Abroad Destinations
                </p>
                <div className="mt-3 grid grid-cols-2 gap-1">
                  {abroadNavigation.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsMobileOpen(false)}
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-brand-charcoal hover:bg-white"
                    >
                      <span>{item.flag}</span>
                      {item.label}
                    </a>
                  ))}
                </div>
                <a
                  href="https://nexteraabroad.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-gold-strong"
                >
                  Visit NextEra Abroad <ExternalLink className="h-3 w-3" />
                </a>
              </div>

              {/* Pathways */}
              <div className="mt-4 rounded-xl border border-brand-line/40 bg-gradient-to-r from-brand-sky/30 to-transparent p-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/60">
                  Admission Pathways
                </p>
                <div className="mt-3 grid gap-1">
                  <a
                    href="https://nexteraabroad.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileOpen(false)}
                    className="flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium text-brand-charcoal hover:bg-white"
                  >
                    Undergraduate Abroad
                    <ExternalLink className="h-3.5 w-3.5 text-brand-gold-strong" />
                  </a>
                  {admissionNavigation.map((item) => (
                    <NavLink
                      key={item.to}
                      className={({ isActive }) =>
                        `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                          isActive
                            ? "text-brand-gold"
                            : "text-brand-charcoal hover:bg-white"
                        }`
                      }
                      to={item.to}
                      onClick={() => setIsMobileOpen(false)}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>

              <PrimaryButton
                className="mt-5 w-full"
                to="/contact"
                onClick={() => setIsMobileOpen(false)}
              >
                Book Counselling
              </PrimaryButton>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </header>
  );
}

export default Navbar;
